import React from "react";
import { X, Radio, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

/**
 * SignalToastOverlay
 * Displays signal update check results as stacked toast cards.
 *
 * Props:
 *   toasts  — array of { id, fetchedAt, expectedTs, results: [{ label, received, error? }] }
 *   onDismiss — (id) => void
 */
export default function SignalToastOverlay({ toasts, onDismiss }) {
  if (!toasts || toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[65] flex flex-col-reverse gap-3 max-w-xs w-full pointer-events-none">
      <div className="flex flex-col-reverse gap-3 pointer-events-auto">
        {toasts.map((toast) => {
          const allReceived = toast.results.every((r) => r.received);
          const noneReceived = toast.results.every((r) => !r.received);
          const partial = !allReceived && !noneReceived;

          const headerColor = allReceived
            ? "bg-emerald-500"
            : noneReceived
            ? "bg-red-500"
            : "bg-amber-500";

          const borderColor = allReceived
            ? "border-emerald-500/30"
            : noneReceived
            ? "border-red-500/30"
            : "border-amber-500/30";

          const statusText = allReceived
            ? "All Signals Received ✓"
            : noneReceived
            ? "No Update Received"
            : "Partial Update";

          const statusTextColor = allReceived
            ? "text-emerald-400"
            : noneReceived
            ? "text-red-400"
            : "text-amber-400";

          return (
            <div
              key={toast.id}
              className={`relative overflow-hidden bg-[#1a1d29] border ${borderColor} rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.6)] animate-signal-slide-in`}
            >
              {/* Top accent bar */}
              <div className={`h-1 w-full ${headerColor}`} />

              <div className="p-3">
                {/* Header row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Radio className={`w-4 h-4 ${allReceived ? "text-emerald-400" : noneReceived ? "text-red-400" : "text-amber-400"}`} />
                    <div>
                      <p className="text-[11px] font-black text-white uppercase tracking-wide">
                        Signal Update Alert
                      </p>
                      <p className="text-[9px] text-gray-500 font-semibold">
                        Fetched at {toast.fetchedAt} · Checking {toast.expectedTs}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onDismiss(toast.id)}
                    className="p-1 hover:bg-gray-800 rounded-md text-gray-500 hover:text-white transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Status summary */}
                <p className={`text-xs font-bold mb-2 ${statusTextColor}`}>
                  {statusText}
                </p>

                {/* Per-signal results */}
                <div className="flex flex-col gap-1">
                  {toast.results.map(({ label, received, error }) => (
                    <div
                      key={label}
                      className="flex items-center justify-between bg-[#0f1116] px-2.5 py-1.5 rounded-lg border border-gray-800"
                    >
                      <div className="flex items-center gap-1.5">
                        {received ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                        ) : error ? (
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                        )}
                        <span className="text-[11px] font-bold text-gray-200 font-mono">
                          {label}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${
                          received
                            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                            : error
                            ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                            : "bg-red-500/15 text-red-400 border-red-500/30"
                        }`}
                      >
                        {received ? "RECEIVED" : error ? "ERROR" : "NO UPDATE"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pulse ring for no-update case */}
              {noneReceived && (
                <div className="absolute inset-0 pointer-events-none border-2 border-red-500/20 rounded-xl animate-pulse" />
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes signal-slide-in {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-signal-slide-in {
          animation: signal-slide-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
}
